import { Flag } from "lucide-react";

import { Card, CardContent } from "components/ui/card";

import { DEFAULT_TRACK_COLOR } from "./AgendaSchedule";
import { useTracks } from "./use-event-content";
import { TrackChampions } from "./YourTrackBanner";

/**
 * The attendee's own track on the Me tab: its colour, its name and its
 * champions, who are the people to find. The page mounts it only for somebody
 * who has a track and only while theme table requests are off; with them on,
 * YourTableCard names the same track and carries the champions instead, so
 * the tab never says it twice.
 *
 * Nothing is drawn while the tracks load or when they cannot be read, and for
 * a track that has since been deleted: the member document and the track list
 * are read at different times, as on the agenda. The champions are the
 * banner's own block, so a link to somebody who has left shows nobody.
 */
export function YourTrackCard({
  eventId,
  trackId,
}: {
  eventId: string;
  trackId: string;
}) {
  const tracks = useTracks(eventId);
  if (tracks.status !== "success") return null;
  const track = tracks.data.find((candidate) => candidate.id === trackId);
  if (!track) return null;

  return (
    <Card data-testid="your-track">
      <CardContent className="space-y-3 py-4">
        <p className="flex items-center gap-2 font-medium text-foreground">
          <Flag
            className="h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          Your track
        </p>
        <p className="flex items-center gap-2 text-foreground">
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: track.color ?? DEFAULT_TRACK_COLOR }}
            aria-hidden
          />
          <span className="min-w-0 font-semibold" data-testid="your-track-name">
            {track.name}
          </span>
        </p>
        <TrackChampions
          eventId={eventId}
          track={track}
          testIdPrefix="your-track"
        />
      </CardContent>
    </Card>
  );
}
